import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useInView } from 'react-intersection-observer'

import { Layout, Header } from 'app/theme/layout'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import ImageHero from 'app/theme/ImageHero'
import { OccasionPageGrid, OccasionPageGridRow } from '../occasions/shared/OccasionPageGrid'
import CuisineTypes from 'app/theme/sections/CuisineTypes'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import FeaturedClients from 'app/theme/sections/FeaturedClients'

import heroImg from 'assets/img/community-builder-hero.jpg'
import communityBuilderProgram1 from 'assets/img/community-builder-1.png'
import communityBuilderProgram2 from 'assets/img/community-builder-2.png'
import communityBuilderProgram3 from 'assets/img/community-builder-3.png'
import communityBuilderProgram4 from 'assets/img/community-builder-4.jpg'
import communityBuilderProgram5 from 'assets/img/community-builder-5.jpg'
import communityBuilderProgram6 from 'assets/img/community-builder-6.jpg'

export default function CommunityBuilderProgramPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>Food Trucks For Apartments, Resident Events, and HOAs - Roaming Hunger</title>
        <meta name="og:title" content="Food Trucks For Apartments, Resident Events, and HOAs"></meta>
        <meta name="description" content="Bring a food truck to your apartment building for your next resident event."></meta>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={heroImg}
          title="Food Trucks For Apartments and Resident Events"
          onButtonClick={openModal}
          description={(
            <div>
              Engage your residents with our Community Builder Program. We’ll bring food trucks
              to you any day of the week and work to make each event a success with dedicated
              management, marketing support, and safety measures.
            </div>
          )}
        />
      </div>
      {!inView && (
        <NavbarFixed
          onButtonClick={openModal}
        />
      )}

      <div className="container text-center py-12 px-8">
        <h2 className="text-navy text-4xl leading-tight font-platform">
          Increase Community Engagement
        </h2>
        <p className="font-light text-gray-900 mt-6">
          With over ten years of experience and 18,000+ food partners all over the US and Canada,
          we’ll create the perfect program for any apartment complex, HOA, or neighborhood.
          All you have to do is tell us which days and times you’d like your resident event
          to be, and we’ll handle the rest.
        </p>
      </div>

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="Keep Residents Safe With Order Ahead"
          content="Enjoy great tasting local dishes without compromising safety. Our mobile food partners have online ordering set up to ensure social distancing practices are in effect. Your tenants can simply select their menu items and pick up their meal in a streamlined and safe process."
          image={communityBuilderProgram1}
          reverse
        />
        <OccasionPageGridRow
          heading="Give Your Tenants Something To Smile About"
          content="Great food is something that everyone can get excited for. Break up the week with a Wednesday mobile BBQ pop-up tent or celebrate the weekend with your building's very own Food Truck Friday, featuring a rotation of different cuisine types. Your tenants will welcome the delicious distraction."
          image={communityBuilderProgram2}
        />
        <OccasionPageGridRow
          heading="Support Local Small Businesses"
          content="We have over 18,000 food truck partners across the US and Canada, many of which are feeling the impact of the Coronavirus pandemic. Support the taco truck you always stopped at after a concert, or the mobile pizza oven that catered your friend’s wedding by bringing them to your apartment complex. Your city’s best food trucks are only a few taps away."
          image={communityBuilderProgram3}
          reverse
        />
        <OccasionPageGridRow
          heading="We’ve Got The Details Covered"
          content="Your dedicated account manager will ensure success at your resident event. Reach out with questions, food truck recommendations, or make changes to your program. When you use Roaming Hunger’s Community Builder Program to bring mobile food providers to your residents, we’re here to make sure everything goes smoothly."
          image={communityBuilderProgram4}
        />
      </OccasionPageGrid>

      <FeaturedClients
        onButtonClick={openModal}
      />

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="Ensure Your Event Is A Success"
          content="Spread the word with custom event fliers created by our design team. This means providing marketing material for you to post, paste, and share."
          image={communityBuilderProgram5}
          reverse
        />
        <OccasionPageGridRow
          heading="Unlimited Food Truck Placements"
          content="Create consistency with a Food Truck Friday rotation, or have a different truck come out three days a week. However many trucks you want is up to you because The Community Builder Program offers unlimited food truck placements at your property."
          image={communityBuilderProgram6}
        />
      </OccasionPageGrid>

      <CuisineTypes baseUrl='/food-truck-catering' />

      <HowItWorks
        heading="How Food Trucks For Apartments and Resident Event Catering Works"
        onButtonClick={openModal}
      />
      <HowItWorksLarge
        heading="How Food Trucks For Apartments and Resident Event Catering Works"
        onButtonClick={openModal}
      />
    </Layout>
  )
}
